import { Note } from "../types/sound";

export interface ChordData {
    root: string;
    notes: Note[];
    name: string;
}

export interface SongProgression {
    id: string;
    name: string;
    artist: string;
    bpm: number;
    key: string;
    chordDurationMs: number; // how long each chord plays
    chords: ChordData[];
    famousSongs: string[];
    description: string;
    playbackStyle: 'block' | 'arpeggio'; // how to play the chords
    arpeggioPattern?: number[]; // indices pattern for arpeggio (e.g., [0,1,2,1] for bottom-middle-top-middle)
}

// Helper to build major and minor chords in C key
const buildChordC = (root: string, type: 'major' | 'minor'): ChordData => {
    const chordNotes: Record<string, { major: Note[], minor: Note[] }> = {
        'C': {
            major: ['C4', 'E4', 'G4'],
            minor: ['C4', 'D#4', 'G4'] // Eb = D#
        },
        'G': {
            major: ['G3', 'B3', 'D4'],
            minor: ['G3', 'A#3', 'D4'] // Bb = A#
        },
        'A': {
            major: ['A3', 'C#4', 'E4'],
            minor: ['A3', 'C4', 'E4']
        },
        'F': {
            major: ['F3', 'A3', 'C4'],
            minor: ['F3', 'G#3', 'C4'] // Ab = G#
        }
    };

    const notes = type === 'major' ? chordNotes[root].major : chordNotes[root].minor;
    const displayName = type === 'major' ? root : root + 'm';

    return {
        root,
        notes,
        name: displayName
    };
};

// Helper to build major and minor chords in A key
const buildChordA = (root: string, type: 'major' | 'minor', displayOverride?: string): ChordData => {
    const chordNotes: Record<string, { major: Note[], minor: Note[] }> = {
        'A': {
            major: ['A3', 'C#4', 'E4'],
            minor: ['A3', 'C4', 'E4']
        },
        'C#': {
            major: ['G#3', 'C#4', 'E4'],
            minor: ['G#3', 'C#4', 'E4'] // C#m with G# in bass
        },
        'E': {
            major: ['E3', 'G#3', 'B3'],
            minor: ['E3', 'G3', 'B3']
        },
        'F#': {
            major: ['F#3', 'C#4', 'F#4'],
            minor: ['F#3', 'C#4', 'F#4'] // F#m without the third (A), just root-fifth-root
        },
        'D': {
            major: ['D3', 'F#3', 'A3'],
            minor: ['D3', 'F3', 'A3']
        }
    };

    const notes = type === 'major' ? chordNotes[root].major : chordNotes[root].minor;
    const displayName = displayOverride || (type === 'major' ? root : root + 'm');

    return {
        root,
        notes,
        name: displayName
    };
};

export const famousSongs: SongProgression[] = [
    {
        id: 'let-it-be',
        name: 'Let It Be',
        artist: 'The Beatles',
        bpm: 72,
        key: 'C',
        chordDurationMs: 1667, // 2 beats at 72 BPM = (60000/72) * 2 ≈ 1667ms
        chords: [
            buildChordC('C', 'major'),
            buildChordC('G', 'major'),
            buildChordC('A', 'minor'),
            buildChordC('F', 'major')
        ],
        famousSongs: [
            'Let It Be - The Beatles',
            'With or Without You - U2',
            'No Woman No Cry - Bob Marley',
            'Don\'t Stop Believin\' - Journey'
        ],
        description: 'The I-V-vi-IV progression in C Major. This is one of the most common chord progressions in pop music. Once you can identify these four chords by ear, you\'ll recognize this pattern in thousands of songs.',
        playbackStyle: 'block'
    },
    {
        id: 'someone-like-you',
        name: "Someone Like You",
        artist: 'Adele',
        bpm: 68,
        key: 'A',
        chordDurationMs: 3529, // 4 beats at 68 BPM = (60000/68) * 4 ≈ 3529ms - enough time for 16 notes
        chords: [
            buildChordA('A', 'major'),
            buildChordA('C#', 'minor'),
            buildChordA('F#', 'minor'),
            buildChordA('D', 'major', 'D/A')
        ],
        famousSongs: [
            'Apologize - OneRepublic',
            'Demons - Imagine Dragons',
            'The Scientist - Coldplay',
            'Halo - Beyoncé'
        ],
        description: 'The I-iii-vi-IV progression in A Major with a descending bass line (A→G#→F#→A). Notice how rhythm transforms the feel - the same intervals and chord types create completely different emotions depending on how they\'re played.',
        playbackStyle: 'arpeggio',
        arpeggioPattern: [0, 1, 2, 1, 0, 1, 2, 1, 0, 1, 2, 1, 0, 1, 2, 1] // Play pattern 4 times = 16 notes before next chord
    }
];
